export function buildPriceState(priceData, sellingPlanId, labels) {
  const selectedPlan = priceData.sellingPlans?.[String(sellingPlanId)];

  if (selectedPlan) {
    return {
      context: labels.subscriptionContext,
      currentPrice: selectedPlan.currentPrice,
      compareAtPrice: selectedPlan.compareAtPrice || '',
      savings: selectedPlan.discountPercentage
        ? labels.subscriptionSavings.replace('[discount]', selectedPlan.discountPercentage)
        : '',
      renewal: selectedPlan.recurringPrice
        ? labels.subscriptionRenewal
            .replace('[price]', selectedPlan.recurringPrice)
            .replace('[frequency]', selectedPlan.recurringFrequency || '')
        : '',
    };
  }

  return {
    context: '',
    currentPrice: priceData.oneTime.currentPrice,
    compareAtPrice: priceData.oneTime.compareAtPrice || '',
    savings: priceData.oneTime.discountPercentage
      ? labels.oneTimeSavings.replace('[discount]', priceData.oneTime.discountPercentage)
      : '',
    renewal: '',
  };
}

function getSellingPlanId(section) {
  if (!section) return '';

  const form = section.querySelector('product-form-component form[data-type="add-to-cart-form"]');
  if (!form) return '';

  const inputs = form.querySelectorAll('[name="selling_plan"]');

  for (const input of inputs) {
    if (input instanceof HTMLInputElement && (input.type === 'radio' || input.type === 'checkbox') && !input.checked) {
      continue;
    }

    if (input instanceof HTMLInputElement || input instanceof HTMLSelectElement) {
      return input.value;
    }
  }

  return '';
}

function updateDisplay(display, priceData) {
  const state = buildPriceState(priceData, getSellingPlanId(display.closest('.shopify-section')), priceData.labels);
  const stateKey = JSON.stringify(state);

  if (display.dataset.priceState === stateKey) return;

  display.dataset.priceState = stateKey;

  const context = display.querySelector('[data-subscription-price-context]');
  const currentPrice = display.querySelector('[data-subscription-current-price]');
  const compareAtPrice = display.querySelector('[data-subscription-compare-price]');
  const savings = display.querySelector('[data-subscription-savings]');
  const renewal = display.querySelector('[data-subscription-renewal]');

  if (context) {
    context.textContent = state.context;
    context.hidden = !state.context;
  }

  if (currentPrice) currentPrice.textContent = state.currentPrice;

  if (compareAtPrice) {
    compareAtPrice.textContent = state.compareAtPrice;
    compareAtPrice.hidden = !state.compareAtPrice;
  }

  if (savings) {
    savings.textContent = state.savings;
    savings.hidden = !state.savings;
  }

  if (renewal) {
    renewal.textContent = state.renewal;
    renewal.hidden = !state.renewal;
  }
}

function initializeDisplay(display) {
  if (display.dataset.subscriptionPriceInitialized === 'true') return;

  const priceDataNode = display.querySelector('[data-subscription-price-data]');
  const section = display.closest('.shopify-section');
  if (!priceDataNode || !section) return;

  let priceData;

  try {
    priceData = JSON.parse(priceDataNode.textContent);
  } catch {
    return;
  }

  display.dataset.subscriptionPriceInitialized = 'true';

  let scheduled = false;
  const scheduleUpdate = () => {
    if (scheduled) return;

    scheduled = true;
    window.requestAnimationFrame(() => {
      scheduled = false;
      updateDisplay(display, priceData);
    });
  };

  section.addEventListener('change', scheduleUpdate, true);
  section.addEventListener('input', scheduleUpdate, true);
  section.addEventListener('click', scheduleUpdate, true);

  new MutationObserver(scheduleUpdate).observe(section, {
    attributes: true,
    attributeFilter: ['checked', 'value'],
    childList: true,
    subtree: true,
  });

  scheduleUpdate();
}

function initializeSubscriptionPrices(root = document) {
  if (root instanceof HTMLElement && root.matches('[data-subscription-price-display]')) {
    initializeDisplay(root);
  }

  root.querySelectorAll?.('[data-subscription-price-display]').forEach(initializeDisplay);
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeSubscriptionPrices(), { once: true });
  } else {
    initializeSubscriptionPrices();
  }

  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof HTMLElement) initializeSubscriptionPrices(node);
      });
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
}
